import React, { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { ThumbnailTypes } from 'src/components/thumbnail/types';
import { CompanionAdsData } from 'src/services/responseTypes/singlePlayResponse';
import { DEVICE_TYPES } from 'src/utils/constants/AppConstants';
import { Commerce } from 'src/utils/constants/CommerceConstants';
import {
  API_STATUS_TYPES,
  FeatureFlagConstants
} from 'src/utils/constants/utilConstants';
import { getRecoThumbnailType } from 'src/utils/getRecoThumbnailTypeHelper';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import { RecommendedContentsProps } from '.';
import { getSessionStorage } from '../../../utils/browserStorageHandler';
import { COMMERCE_WISHLISTED_PRODUCTS } from '../../../utils/constants/localStorageConstants';
import CommerceSideBar from '../CommerceSideBar';
import CommerceBanner from '../ConsumptionMetaData/CommerceBanner';
import { sideBarWrapper } from './RecommendedWrapperStyles';

// Lazy loading components
const RecommendedContents = dynamic(() => import('.'), {
  loading: () => <SkeletonRecoTray type={ThumbnailTypes.vertical} />,
  ssr: false
});
const SkeletonRecoTray = dynamic(
  () => import('../../SkeletonLoader/SkeletonRecoTray'),
  {
    ssr: false
  }
);
const AdComponent = dynamic(
  () => import('src/components/adComponent/AdComponent'),
  {
    ssr: false
  }
);
interface RecommendedWrapperProps extends RecommendedContentsProps {
  status: API_STATUS_TYPES;
  recommendedContents: any;
  companionAds: CompanionAdsData;
  contentType: string;
  commerceLayout?: string;
  showWishlist: boolean;
  showWishlistHandler: () => void;
  commerceCloseIconHandler?: () => void;
  handleCommerceLayout: () => void;
}

const RecommendedWrapper: React.FC<RecommendedWrapperProps> = (props) => {
  const {
    appConfig,
    remoteConfig,
    companionAdState,
    pageLoadPingUrl,
    translations,
    commerceComponentData: commerceData,
    commerceWishlistedProductsLength,
    setCommerceWishlistedProductsLength,
    commerceComponentData
  } = useAppStore();

  const {
    assetDetails,
    companionAds,
    recommendedContents,
    sectionHeight,
    commerceLayout = '',
    showWishlist = false,
    showWishlistHandler,
    commerceCloseIconHandler,
    handleCommerceLayout
  } = props;
  const router = useRouter();
  const { recommendedContentData, thumbnailType } = recommendedContents || {
    recommendedContentData: null,
    thumbnailType: getRecoThumbnailType(router?.asPath)
  };
  const commerceDataLength = commerceComponentData?.length || 0;
  const productsWishlisted =
    getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || [];
  const [commerceFeatureFlag, setCommerceFeatureFlag] =
    useState<boolean>(false);

  useEffect(() => {
    const commerce_feature_flag = isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.CONFIG_COMMERCE
    );
    setCommerceFeatureFlag(commerce_feature_flag);
  }, [remoteConfig?.length]);

  return (
    <div
      css={sideBarWrapper(
        sectionHeight,
        companionAdState.companionAdLoaded,
        commerceLayout === Commerce.SIDE_BAR
      )}
      data-testid="reco-container">
      {appConfig.deviceType === DEVICE_TYPES.DESKTOP &&
      companionAdState.isAdPlayingOnPlayer ? (
        <div className="companionAdATF">
          <div className="content">
            <AdComponent
              key={companionAds?.div_id}
              adUnit={companionAds?.ad_tag}
              id={companionAds?.div_id}
              dimension={companionAds?.ad_dimension}
              adTarget={companionAds?.ad_targeting}
              type="companionAd"
              position={0}
              pageType="consumption"
            />
          </div>
        </div>
      ) : null}
      {commerceFeatureFlag &&
        commerceDataLength > 0 &&
        appConfig?.deviceType === DEVICE_TYPES.DESKTOP && (
          <CommerceBanner
            showWishlist={showWishlistHandler}
            handleCommerceLayout={handleCommerceLayout}
          />
        )}
      {commerceLayout === Commerce.SIDE_BAR ? (
        <CommerceSideBar
          translations={translations}
          showWishlist={showWishlist}
          pageLoadPingUrl={pageLoadPingUrl}
          commerceWishlistedProductsLength={commerceWishlistedProductsLength}
          data={showWishlist ? productsWishlisted : commerceData}
          handleCommerceLayout={handleCommerceLayout}
          closeIconClick={commerceCloseIconHandler}
          setCommerceWishlistedProductsLength={
            setCommerceWishlistedProductsLength
          }
        />
      ) : (thumbnailType === ThumbnailTypes.upnext && recommendedContentData) ||
        (recommendedContentData &&
          Object.keys(recommendedContentData).length) ? (
        <RecommendedContents
          type={thumbnailType}
          recommendedContents={recommendedContentData[1]}
          sectionHeight={sectionHeight}
          assetDetails={assetDetails}
        />
      ) : recommendedContentData === null ? (
        <SkeletonRecoTray type={thumbnailType} />
      ) : null}
    </div>
  );
};

export default React.memo(RecommendedWrapper);
